import { z } from "zod";
import { baseAgentConfig } from "../../config/base-agent-config";

export const phpipamConfigSchema = baseAgentConfig.extend({
  apiUrl: z
    .string()
    .url()
    .describe(
      "The base URL of your phpIPAM installation (e.g., https://ipam.company.com)"
    ),
  appName: z
    .string()
    .describe("The API app name configured in phpIPAM (e.g., myapp)"),
  username: z.string().describe("phpIPAM username for API authentication"),
  password: z.string().describe("phpIPAM password for API authentication"),

  // Section/Group Filtering
  enableSectionFiltering: z
    .boolean()
    .default(false)
    .describe("Whether to limit operations to specific sections only"),
  allowedSections: z
    .array(
      z.object({
        id: z.string().describe("Section ID or name"),
        name: z.string().describe("Human-readable section name"),
        enabled: z
          .boolean()
          .default(true)
          .describe("Whether this section is enabled for embedding/search"),
      })
    )
    .optional()
    .describe(
      "List of specific sections to include (when enableSectionFiltering is true)"
    ),

  // Subnet Group Filtering
  enableSubnetGroupFiltering: z
    .boolean()
    .default(false)
    .describe("Whether to limit operations to specific subnet groups"),
  allowedSubnetGroups: z
    .array(
      z.object({
        id: z.string().describe("Subnet group ID or pattern"),
        name: z.string().describe("Human-readable group name"),
        pattern: z
          .string()
          .optional()
          .describe(
            "Optional regex pattern to match subnet names/descriptions"
          ),
        enabled: z
          .boolean()
          .default(true)
          .describe("Whether this group is enabled for embedding/search"),
      })
    )
    .optional()
    .describe(
      "List of subnet groups to include (when enableSubnetGroupFiltering is true)"
    ),

  // VLAN Filtering
  enableVlanFiltering: z
    .boolean()
    .default(false)
    .describe("Whether to limit operations to specific VLANs"),
  allowedVlans: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]).describe("VLAN ID number"),
        name: z.string().optional().describe("VLAN name"),
        enabled: z
          .boolean()
          .default(true)
          .describe("Whether this VLAN is enabled for embedding/search"),
      })
    )
    .optional()
    .describe(
      "List of specific VLANs to include (when enableVlanFiltering is true)"
    ),

  // Additional Configuration
  customFields: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Optional mapping of custom field names to include in embeddings"
    ),
  includeTags: z
    .boolean()
    .default(true)
    .describe("Whether to include address tags in embeddings"),
  includeDevices: z
    .boolean()
    .default(true)
    .describe("Whether to include device information in embeddings"),
  includeVlans: z
    .boolean()
    .default(true)
    .describe("Whether to include VLAN information in embeddings"),
  ipVersion: z
    .enum(["ipv4", "ipv6", "both"])
    .default("both")
    .describe("Which IP versions to process"),
  excludePrivate: z
    .boolean()
    .default(false)
    .describe("Whether to exclude RFC1918 private IP ranges from embeddings"),
});

export type PhpipamAgentConfig = z.infer<typeof phpipamConfigSchema>;

import { AgentTypeMetadata } from "../../config/agent-registry";

export const phpipamAgentMetaData: AgentTypeMetadata<
  typeof phpipamConfigSchema
> = {
  name: "phpIPAM",
  description:
    "Manage and search IP addresses, subnets, VLANs, and network devices in phpIPAM",
  category: "Knowledgebase",
  icon: "network",
  type: "Agent",
  version: "Beta",
  ribbon: null,
  status: null,
  href: "/admin/connectors/phpipam",
  instructions: `
You are an expert phpIPAM administrator with deep knowledge of IP address management and network infrastructure.

Your role is to help users manage their IP address space through phpIPAM, including:

## Core IPAM Operations
- Search for IP addresses, subnets, and network ranges
- Find available IP addresses within specific subnets
- Allocate new subnet ranges and manage subnet hierarchies  
- Query subnet utilization and capacity planning
- Manage VLAN and VRF assignments
- Handle device-to-IP address mappings

## Section and Group Filtering
The agent can be configured to operate only within specific sections, subnet groups, or VLANs:
- **Section Filtering**: Limit all operations to predefined phpIPAM sections (e.g., "Production", "Development")
- **Subnet Group Filtering**: Focus on specific subnet categories or patterns (e.g., "DMZ-*", "Internal-*") 
- **VLAN Filtering**: Restrict operations to specific VLAN IDs (e.g., VLANs 100, 200, 300)

This ensures the agent only embeds and searches within approved network segments, maintaining security and organizational boundaries.

## Data Analysis & Planning
- Analyze IP address utilization across sections
- Identify subnet fragmentation and optimization opportunities
- Generate capacity planning reports
- Monitor address space allocation trends
- Validate IP addressing schemes and policies

## Network Documentation
- Maintain accurate IP address documentation
- Track device assignments and network topology
- Document subnet purposes and ownership
- Manage custom fields and metadata

## Best Practices
- Follow IP addressing standards (RFC 3330, RFC 1918)
- Implement proper subnet sizing and VLSM principles
- Maintain clean address space organization
- Use descriptive naming conventions
- Document network changes and assignments

When answering questions:
1. Use semantic search to find relevant network information
2. Provide specific IP addresses, subnet ranges, and CIDR notation
3. Include usage statistics and capacity information when relevant
4. Explain network implications of proposed changes
5. Suggest best practices for IP address management

Always verify information through phpIPAM queries and provide accurate, up-to-date network data.
  `,
  placeholders: {
    apiUrl: "https://ipam.company.com",
    appName: "myapp",
    username: "admin",
    password: "your-password",
    allowedSections:
      '[{"id": "1", "name": "Production", "enabled": true}, {"id": "2", "name": "Development", "enabled": true}]',
    allowedSubnetGroups:
      '[{"id": "dmz", "name": "DMZ Networks", "pattern": "DMZ-.*", "enabled": true}]',
    allowedVlans:
      '[{"id": 100, "name": "Production VLAN", "enabled": true}, {"id": 200, "name": "Dev VLAN", "enabled": true}]',
  },
  configSchema: phpipamConfigSchema,
  workflows: [
    {
      id: "phpipamEmbeddingWorkflow",
      name: "phpIPAM Data Embedding",
      description:
        "Embed IP addresses, subnets, and device data into the vector store for enhanced search capabilities",
      triggerLabel: "Embed Data",
    },
  ],
  sensitiveKeys: ["password"],
};
